import * as Yup from "yup";
import { EppsLoanInformation } from "../../../../models/epps-models";
import { isGovtLoan } from "./utils";
const getCustomFieldValidations = (formValues) => {
  const feeAmount = formValues("loanInformation.feeAmount");
  const financed = formValues("loanInformation.feeAmountFinanced");
  const paidInCash = formValues("loanInformation.feeAmountPaidinCash");
  const standardProducts = formValues("standardProducts") ?? [];
  const specialProducts = formValues("specialProducts") ?? [];
  const productType = formValues("productType");
  const term = formValues("loanInformation.term");
  const totalMortgageAmount = formValues("loanInformation.totalMortgageAmount");
  const cashOut = formValues("loanInformation.cashOut");
  const loanPurpose = formValues("loanInformation.loanPurpose");
  const helocProducts = [66, 21, 64, 117, 77, 90, 65, 67];
  const productIsHeloc = helocProducts.some(
    (prod) => productType?.includes(prod)
  );
  const isGovt = isGovtLoan(standardProducts, specialProducts);
  const feeMax = isGovt ? feeAmount : Number.MAX_SAFE_INTEGER;
  const feeMaxMsg = `Must be less than or equal to $${feeAmount}`;
  const combinedFeeTest = () => {
    if (!isGovt || typeof feeAmount !== "number" || typeof financed !== "number" || typeof paidInCash !== "number") {
      return true;
    }
    return Math.abs(financed + paidInCash - feeAmount) < 0.01;
  };
  const validations = [
    {
      path: "standardProducts",
      label: "Standard Products",
      validation: Yup.mixed().nullable().test(
        "multiple-fha-va-usda-check",
        "Only one of FHA, VA or USDA can be selected.",
        () => {
          if ([
            EppsLoanInformation.StandardProducts.FHA,
            EppsLoanInformation.StandardProducts.VA,
            EppsLoanInformation.StandardProducts.USDA
          ].filter((num) => standardProducts?.includes(num)).length >= 2) {
            return false;
          }
          return true;
        }
      ).test(
        "at-least-one-product-check",
        "At least one Standard or Special product is required.",
        () => {
          return standardProducts.length > 0 || specialProducts.length > 0;
        }
      )
    },
    {
      path: "specialProducts",
      label: "Special Products",
      validation: Yup.mixed().nullable().test(
        "at-least-one-product-check",
        "At least one Standard or Special product is required.",
        () => {
          return standardProducts.length > 0 || specialProducts.length > 0;
        }
      )
    },
    {
      path: "loanInformation.feeAmountFinanced",
      label: "Fee Amount Financed",
      validation: Yup.number().nullable().max(feeMax, feeMaxMsg).test("combined-financed-cash-check", `Financed Amount + Paid in Cash must equal $${feeAmount}`, combinedFeeTest)
    },
    {
      path: "loanInformation.feeAmountPaidinCash",
      label: "Fee Amount Paid In Cash",
      validation: Yup.number().nullable().max(feeMax, feeMaxMsg).test("combined-financed-cash-check", `Financed Amount + Paid in Cash must equal $${feeAmount}`, combinedFeeTest)
    },
    {
      path: "productType",
      label: "Product Type",
      validation: Yup.mixed().nullable().test(
        "product-or-term-required",
        "Either enter a Custom Term or choose a Loan Term",
        () => {
          const hasLoanterm = Array.isArray(productType) && productType.length > 0;
          const hasCustomTerm = term != null && term !== "";
          const hasBoth = hasLoanterm && hasCustomTerm;
          const hasNeither = !hasLoanterm && !hasCustomTerm;
          if (hasNeither || hasBoth) return false;
          return true;
        }
      )
    },
    {
      path: "loanInformation.term",
      label: "Custom Term",
      validation: Yup.number().nullable().min(1, "Custom Term must be an integer value from 1 to 360").max(360, "Custom Term must be an integer value from 1 to 360")
    },
    {
      path: "loanInformation.cashOut",
      label: "Cash Out",
      validation: Yup.mixed().nullable().test(
        "cash-out-exceeds-total-check",
        "Cash out amount exceeds total loan amount",
        () => {
          if (loanPurpose === 3) {
            const hasHelocLoanTerm = Array.isArray(productType) && productType.length > 0 && productIsHeloc;
            if (typeof cashOut !== "number" || typeof totalMortgageAmount !== "number" || hasHelocLoanTerm) {
              return true;
            }
            return cashOut <= totalMortgageAmount;
          }
          return true;
        }
      )
    },
    {
      path: "borrowerFinancialHistory.docTypeEmployment",
      label: "Employment",
      validation: Yup.mixed().required("Employment status is required")
    },
    {
      path: "borrowerFinancialHistory.docTypeAsset",
      label: "Asset",
      validation: Yup.mixed().required("Asset status is required")
    },
    {
      path: "borrowerFinancialHistory.docTypeIncome",
      label: "Income",
      validation: Yup.mixed().required("Income status is required")
    }
  ];
  const delinquencyFields = [
    { path: "borrowerFinancialHistory.30DaysMortgageLatestin12Months", label: "30-day Mortgage Delinquencies in the last 12 months" },
    { path: "borrowerFinancialHistory.60DaysMortgageLatestin12Months", label: "60-day Mortgage Delinquencies in the last 12 months" },
    { path: "borrowerFinancialHistory.90DaysMortgageLatestin12Months", label: "90-day Mortgage Delinquencies in the last 12 months" },
    { path: "borrowerFinancialHistory.30DaysMortgageLatestin24Months", label: "30-day Mortgage Delinquencies in the prior 13-24 months" },
    { path: "borrowerFinancialHistory.60DaysMortgageLatestin24Months", label: "60-day Mortgage Delinquencies in the prior 13-24 months" },
    { path: "borrowerFinancialHistory.90DaysMortgageLatestin24Months", label: "90-day Mortgage Delinquencies in the prior 13-24 months" }
  ];
  delinquencyFields.forEach(({ path, label }) => {
    const value = formValues(path);
    if (value !== null && value !== void 0 && value !== "") {
      validations.push({
        path,
        label,
        validation: Yup.number().nullable().min(0, `Please enter the number of ${label} (12 max).`).max(12, `Please enter the number of ${label} (12 max).`)
      });
    }
  });
  if (productIsHeloc) {
    validations.push({
      path: "loanInformation.loanAmountMaxLine",
      label: "Max Loan Amount",
      validation: Yup.number().required(
        "Max Loan Amount is required for HELOC products"
      )
    });
  }
  return validations;
};
export {
  getCustomFieldValidations
};
